import {
  isLargeOperation,
  isParameterValueCreateOperation,
  type LargeOperation,
} from '@festina/configuration-general-types/configuration'
import { useLazyFetchLargeOperations } from '@festina-life/api'
import { type ConfigurableServer, notEmpty } from '@festina-life/core'
import { useCallback } from 'react'

import type {
  ParameterValueEntityOperation,
  ParameterValueOperation,
} from '../parameterOperations/types'
import type { OperationWithSourceInfo } from '../types'
import { mapLargeOperationsToResolvedValueOperations } from './helpers'

export type ResolveValueOperationsProps = {
  allValues: OperationWithSourceInfo<ParameterValueOperation>[]
  server: ConfigurableServer
}

/**
 * Fetches the payloads of the large operations among the given value operations and
 * inlines them, leaving every other operation untouched. Amend operations pass through
 * unchanged: deciding what to do with them belongs to the consumer, not here.
 */
export const useLazilyResolveValueOperations = () => {
  const { mutateAsync, isPending } = useLazyFetchLargeOperations()

  const resolve = useCallback(
    async ({
      allValues,
      server,
    }: ResolveValueOperationsProps): Promise<
      OperationWithSourceInfo<ParameterValueEntityOperation>[]
    > => {
      const largeValuesForId = allValues?.filter(
        (
          editableOperation,
        ): editableOperation is OperationWithSourceInfo<LargeOperation> =>
          isLargeOperation(editableOperation.internalOperation),
      )

      const fetchLargeOperationIds = (largeValuesForId ?? [])
        .map((editableOperation) => editableOperation.internalOperation._id)
        .filter(notEmpty)

      const resolvedLargeOperations = await mutateAsync({
        server,
        operationIds: fetchLargeOperationIds,
        validate: isParameterValueCreateOperation,
      })

      return mapLargeOperationsToResolvedValueOperations({
        values: allValues ?? [],
        largeOperations: resolvedLargeOperations ?? [],
      })
    },
    [mutateAsync],
  )

  return { mutateAsync: resolve, isPending }
}
